// Форма ответа на исполненное описание.
//
// Типы вынесены из сервиса намеренно: их читает и клиент, а сервис тянет
// Prisma, которой в браузерном бандле быть не должно.
//
// Ответ самодостаточен — в нём есть и подписи колонок, и значения перечислений,
// и уже разрешённые ссылки. Рендереру не нужно ничего доискивать: сколько бы
// сущностей ни добавилось в каталог, отрисовка не меняется.
import type { EnumOption, FieldType } from './catalog.js';

/** Ссылка на другую запись — отдана уже разрешённой, с готовой подписью. */
export interface ViewCellRef {
  id: string;
  label: string;
  /** Ключ сущности каталога либо null, если цель вне каталога (пользователь). */
  entity: string | null;
  /** Маршрут карточки, если он есть. */
  href: string | null;
  /**
   * Привязка взята не с самой записи, а с соседней — и здесь стоит ключ сущности,
   * через которую она пришла (`contact`, `deal`, `document`, `task`, `meeting`).
   * Пометка нужна обеим сторонам: интерфейс рисует такую ссылку приглушённо, как в
   * гриде сделок, а ассистент по ней отличает «компания проставлена у задачи» от
   * «подтянута через сделку» — без пометки он выдаёт второе за первое.
   *
   * Раньше здесь был флаг `inherited: true`, и подпись в интерфейсе была прибита
   * гвоздями: «связь через контакт». С появлением четырёх путей у одного поля это
   * стало ложью в трёх случаях из четырёх, поэтому путь называется явно.
   */
  inheritedVia?: string;
  /**
   * Прежняя форма той же пометки, до появления нескольких путей. Новый код её не
   * пишет, но она лежит в `structuredPayload` уже сохранённых сообщений чата:
   * представления персистятся вместе с ходом. Уберёшь — старые диалоги молча
   * потеряют приглушение, и унаследованная привязка станет там выглядеть
   * проставленной. Для тех данных путь был ровно один, контакт.
   */
  inherited?: true;
}

export type ViewCell =
  | string
  | number
  | boolean
  | null
  | string[]
  | ViewCellRef
  /** Все привязки записи: у заметки их может быть несколько сразу. */
  | ViewCellRef[];

export interface ViewColumn {
  key: string;
  label: string;
  type: FieldType;
  options?: readonly EnumOption[];
}

export interface ViewRow {
  id: string;
  cells: Record<string, ViewCell>;
}

export interface TableResult {
  kind: 'table';
  title: string;
  entity: string;
  columns: ViewColumn[];
  rows: ViewRow[];
}

export interface StatResult {
  kind: 'stat';
  title: string;
  entity: string;
  agg: 'count' | 'sum' | 'avg';
  value: number;
  /** Тип для отрисовки: сумма денег и количество показываются по-разному. */
  valueType: 'number' | 'money';
}

export interface RecordResult {
  kind: 'record';
  title: string;
  entity: string;
  id: string;
  href: string | null;
  fields: Array<ViewColumn & { value: ViewCell }>;
}

export interface CalendarEventResult {
  id: string;
  title: string;
  start: string;
  end: string;
}

export interface CalendarResult {
  kind: 'calendar';
  title: string;
  entity: string;
  events: CalendarEventResult[];
}

export interface GroupBucket {
  /** Значение разреза: подпись перечисления, ссылка или строка. */
  key: ViewCell;
  value: number;
}

export interface GroupResult {
  kind: 'group';
  title: string;
  entity: string;
  by: ViewColumn;
  agg: 'count' | 'sum' | 'avg';
  valueType: 'number' | 'money';
  buckets: GroupBucket[];
  /**
   * Разрез посчитан по части записей — их оказалось больше потолка сканирования.
   *
   * Появляется только у разрезов по привязке с наследованием: их считает
   * приложение, а не БД (`groupBy` по вычисляемому выражению не умеет). Молчать об
   * этом нельзя, и мало сказать «сумма неполная»: строки за потолком могли бы
   * переставить корзины местами, поэтому недостоверен ещё и ПОРЯДОК. И интерфейс,
   * и ассистент обязаны сказать это вслух.
   */
  truncated?: { scanned: number; total: number };
}

export type ViewBlockResult =
  | TableResult
  | StatResult
  | RecordResult
  | CalendarResult
  | GroupResult;

export interface ViewResult {
  title: string;
  blocks: ViewBlockResult[];
}

/** Ссылка ли это — отличаем от строки, массива меток и числа. */
export function isCellRef(cell: ViewCell): cell is ViewCellRef {
  return typeof cell === 'object' && cell !== null && !Array.isArray(cell) && 'label' in cell;
}
